import {useAppSelector, useBreakPoint, useFeatureToggle} from '@hooks';
import {compareExtrasWorkdayFirstAlphabetical, getExtras} from '@lib/store';
import {
  ExtrasOverview,
  ConnectionOverview,
  EmployeeCommunicationAnnouncementTile,
  NextWorkdaysSimplifiedSideOverview,
  Tile,
} from '@organisms';
import {format} from 'date-fns';

/**
 * This sidebar takes care of its own content. It tries to show content as soon as it's available, but takes care to only _append_ content.
 * This prevents vertical layout shifts that might cause the thing users are looking at / interacting with to move down.
 */
export const Sidebar = ({date}: {date: Date}) => {
  const bp = useBreakPoint();
  const dateKey = format(date, 'yyyy-MM-dd');

  const {ExtrasFeature: extrasEnabled, EmployeeCommunication: employeeCommunicationEnabled} = useFeatureToggle();
  const extrasLoading = useAppSelector((state) => state.data.extras.isLoading);

  const relevantExtras = useAppSelector(getExtras)
    .filter((e) => e.type === 'Workday' || e.type === 'UserProfile')
    // Note: should we respect server-side ordering instead?
    .sort(compareExtrasWorkdayFirstAlphabetical);

  // On mobile, connections section gets its own tile
  // On wider breakpoints, only show it once extras have loaded
  const showConnections = bp !== 'small' && !extrasLoading;

  return (
    <>
      {employeeCommunicationEnabled && <EmployeeCommunicationAnnouncementTile />}

      <NextWorkdaysSimplifiedSideOverview referenceDate={date} />

      {extrasEnabled ? (
        <ExtrasOverview
          extras={relevantExtras}
          loading={extrasLoading}
        />
      ) : null}
      {showConnections ? (
        <Tile>
          <ConnectionOverview dateKey={dateKey} />
        </Tile>
      ) : null}
    </>
  );
};
